import { css } from "@emotion/react";
import {
  DeepKeyPaths,
  DetailsSummary,
  objectLookup,
  StatusDot,
  Tree
} from "@madhive/kit-ui";
import { useMemo } from "react";
import AgentMessageStatusDotDescriptions from "./AgentMessage.StatusDotDescriptions";
import { AgentMessageDataSuggestionProps } from "./types";
import { AgentEventDataUnresolved, DoNotCare } from "@madhive/mad-sdk";

/**
 * Displays a tree view of suggested data, with additional information about unresolved fields and validation errors.
 * The tree view is rendered within a collapsible details summary component.
 * This component is useful for displaying structured data suggestions in a user-friendly manner,
 * allowing users to easily identify and resolve issues with the data.
 */
function AgentMessageDataSuggestion<T>({
  suggestedData,
  formData,
  validationErrors,
  unresolved = [],
  treeProps,
  showStatusDescriptions,
  ...detailsSummaryProps
}: AgentMessageDataSuggestionProps<T>) {
  const unresolvedMap = useMemo(() => {
    const map = new Map<string, AgentEventDataUnresolved>();
    unresolved.forEach(item => {
      const formValue = objectLookup(formData, item.key);
      if (formValue === undefined || formValue === "") {
        map.set(item.key, item);
      }
    });
    return map;
  }, [unresolved, formData]);

  const fixedKeys = useMemo(() => {
    const keys = new Set<DeepKeyPaths<T>>([
      ...(treeProps?.fixedKeys || []),
      ...(Array.from(unresolvedMap.keys()) as DeepKeyPaths<T>[])
    ]);
    if (validationErrors) {
      Array.from(validationErrors.keys()).forEach(key =>
        keys.add(key as DeepKeyPaths<T>)
      );
    }
    return Array.from(keys);
  }, [unresolvedMap, validationErrors, treeProps?.fixedKeys]);

  const treeData = useMemo(
    () => ({
      ...suggestedData,
      ...formData,
      ...((Object.fromEntries(
        [...unresolvedMap.entries()].map(([key, value]) => [key, value.value])
      ) || {}) as T)
    }),
    [suggestedData, formData, unresolvedMap]
  );

  if (!suggestedData && !formData) {
    return null;
  }
  return (
    <>
      {showStatusDescriptions && <AgentMessageStatusDotDescriptions />}
      <DetailsSummary {...detailsSummaryProps}>
        <div
          css={css`
            padding-left: var(--spacing-4);
          `}
        >
          <Tree<T>
            {...treeProps}
            lookupPrefix={key =>
              lookupTreeLabelPrefix(key, {
                unresolved: unresolvedMap,
                validationErrors,
                formData
              })
            }
            fixedKeys={fixedKeys}
            data={treeData}
            excludeEmpty
          />
        </div>
      </DetailsSummary>
    </>
  );
}

function lookupTreeLabelPrefix<T>(
  key: string,
  {
    unresolved,
    validationErrors,
    formData
  }: Pick<
    AgentMessageDataSuggestionProps<T>,
    "validationErrors" | "formData"
  > & {
    unresolved: Map<string, AgentEventDataUnresolved>;
  }
) {
  const formValue: DoNotCare = objectLookup(formData, key);
  if (unresolved?.has(key) && (formValue === undefined || formValue === "")) {
    return <StatusDot intent="warning" />;
  }
  if (validationErrors?.has(key)) {
    return <StatusDot intent="error" />;
  }
  return undefined;
}

AgentMessageDataSuggestion.displayName = "AgentMessage.DataSuggestion";

export default AgentMessageDataSuggestion;
